import { useEffect, useState, useRef } from 'react'
import { X } from 'lucide-react'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { showConfirm } from '@/lib/alerts'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/use-media-query'

export type CustomDialogProps = {
  children?: Readonly<React.ReactNode>
  persistent?: boolean
  open: boolean
  title?: string
  description?: string
  className?: string
  confirmationBeforeClose?: boolean
  hideTitle?: boolean
  onClose?: () => void
}

export const CustomDialog = ({ children, ...props }: CustomDialogProps): JSX.Element => {
  const dialogRef = useRef(null)
  const [isOpen, setIsOpen] = useState(props.open)
  const isMdAndDown = useMediaQuery('(max-width:1123px)')

  const _handleClose = async () => {
    setIsOpen(true)
    if (!props.confirmationBeforeClose && props.onClose) props.onClose()
    else {
      const { isConfirmed } = await showConfirm('Deseja fechar?')
      if (isConfirmed && props.onClose) props.onClose()
    }
  }

  useEffect(() => {
    setIsOpen(props.open)
  }, [props.open])

  useEffect(() => {
    if (!isOpen && props.onClose) {
      if (props.confirmationBeforeClose) _handleClose()
      else props.onClose()
    }
  }, [isOpen])

  useEffect(() => {
    function handleClickOutside(e) {
      if (dialogRef.current && !props.persistent && !dialogRef.current?.contains?.(e.target)) {
        props?.onClose?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div ref={dialogRef} className="z-[1]">
      <Dialog open={isOpen}>
        <DialogContent className={cn(`bg-card rounded-2xl border border-border focus-visible:outline-none ${props.className || ''} lg:max-w-screen-lg overflow-y-auto max-h-screen`, 'w-full', isMdAndDown ? 'min-w-full' : 'min-w-[1000px]')}>
          <DialogHeader className={` ${!props.hideTitle ? 'flex flex-col w-full max-h-fit items-start justify-between gap-y-1 pb-3 border-b border-border' : ''}`}>
            {props.hideTitle ? (
              <div className="sr-only">
                <DialogTitle>{props.title}</DialogTitle>
                <DialogDescription>{props.description}</DialogDescription>
              </div>
            ) : (
              <>
                <DialogTitle className="flex w-full justify-between items-center text-foreground text-xl font-semibold">
                  {props.title}
                  <DialogClose onClick={_handleClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                    <X className="size-4" />
                  </DialogClose>
                </DialogTitle>
                {props.description && <DialogDescription className="text-muted-foreground text-sm">{props.description}</DialogDescription>}
              </>
            )}
          </DialogHeader>

          {children}
        </DialogContent>
      </Dialog>
    </div>
  )
}
